import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import { getListings, createListing, updateListing, deleteListing } from '../../services/listings';
import ListingCard from './ListingCard';
import ListingForm from './ListingForm';

const listingTypes = [
  { value: 'active-sales', label: 'Active Sales' },
  { value: 'rentals', label: 'Rentals' },
  { value: 'short-term-rentals', label: 'Short Term Rentals' },
  { value: 'past-sales', label: 'Past Sales' },
  { value: 'past-rentals', label: 'Past Rentals' },
];

const ListingsManager = () => {
  const [tabValue, setTabValue] = useState(0);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const currentType = listingTypes[tabValue].value;

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getListings(currentType);
      setListings(data);
    } catch (error) {
      console.error('Error fetching listings:', error);
      alert('Error loading listings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentType]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCreate = () => {
    setEditingListing(null);
    setFormOpen(true);
  };

  const handleEdit = (listing) => {
    setEditingListing(listing);
    setFormOpen(true);
  };

  const handleFormSubmit = async (values) => {
    try {
      if (editingListing) {
        await updateListing(currentType, editingListing._id, values);
      } else {
        await createListing(currentType, values);
      }
      fetchListings();
      setFormOpen(false);
      setEditingListing(null);
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteClick = (id) => {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteListing(currentType, deletingId);
      fetchListings();
      setDeleteDialogOpen(false);
      setDeletingId(null);
    } catch (error) {
      console.error('Error deleting listing:', error);
      alert('Error deleting listing. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
          borderBottom: '1px solid #e0e0e0',
          pb: 2,
        }}
      >
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 400,
              fontSize: '0.875rem',
              minHeight: 40,
              color: '#6b7280',
              px: 2,
              '&:hover': {
                color: '#1a1d29',
              },
            },
            '& .Mui-selected': {
              color: '#1a1d29',
              fontWeight: 500,
            },
            '& .MuiTabs-indicator': {
              height: 2,
              bgcolor: '#1a1d29',
            },
          }}
        >
          {listingTypes.map((type) => (
            <Tab key={type.value} label={type.label} />
          ))}
        </Tabs>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={{
            borderRadius: 1,
            px: 2.5,
            py: 1,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            bgcolor: '#1a1d29',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#2d3142',
              boxShadow: 'none',
            }
          }}
        >
          New Listing
        </Button>
      </Box>

      {listings.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            px: 2,
            bgcolor: '#ffffff',
            borderRadius: 1,
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="body1" sx={{ color: '#6b7280', fontWeight: 400, mb: 1 }}>
            No listings found
          </Typography>
          <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: '0.875rem' }}>
            Create your first listing to get started
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {listings.map((listing) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={listing._id}>
              <ListingCard
                listing={listing}
                listingType={currentType}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <ListingForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingListing(null);
        }}
        onSubmit={handleFormSubmit}
        listingType={currentType}
        listing={editingListing}
      />

      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            minWidth: 400,
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 500, pb: 1.5, fontSize: '1.125rem', color: '#1a1d29' }}>
          Delete Listing
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography sx={{ color: '#6b7280', fontSize: '0.875rem', lineHeight: 1.6 }}>
            Are you sure you want to delete this listing? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            variant="text"
            sx={{ 
              minWidth: 80,
              textTransform: 'none',
              color: '#6b7280',
              fontWeight: 400,
              '&:hover': {
                bgcolor: '#f5f7fa',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained"
            sx={{ 
              minWidth: 80,
              textTransform: 'none',
              bgcolor: '#dc2626',
              fontWeight: 500,
              '&:hover': {
                bgcolor: '#b91c1c',
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListingsManager;

